import { useState } from 'react'
import { Pencil } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useUpdateDriverDetails } from '../../hooks/backend/drivers'
import { writeErrorMessage } from '../../utils/writeError'
import type { SelectedApplication } from '../../utils/interviews'

function nullIfEmpty(value: string): string | null {
  const t = value.trim()
  return t === '' ? null : t
}

// Only include keys the user actually set — the driver row's current values are
// not readable to this login, so blank fields must be left untouched, never
// overwritten with null.
function addIfSet(patch: Record<string, unknown>, key: string, value: string) {
  const v = value.trim()
  if (v !== '') patch[key] = v
}

const EMPLOYMENT_TYPE_OPTIONS = [
  { code: 'part_time', label: 'Part time' },
  { code: 'full_time', label: 'Full time' },
]

export function EditDriverDialog({
  app,
  onSaved,
}: {
  app: SelectedApplication
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const update = useUpdateDriverDetails()
  const hasDriverRow = app.is_driver === true

  // Person contact — prefilled from the application.
  const [phone, setPhone] = useState(app.primary_phone ?? '')
  const [email, setEmail] = useState(app.primary_email ?? '')
  const [whatsapp, setWhatsapp] = useState(app.whatsapp_phone ?? '')

  // Driver-row fields — not readable, start blank; only sent when filled.
  const [licenseNumber, setLicenseNumber] = useState('')
  const [licenseExpiry, setLicenseExpiry] = useState('')
  const [pdpNumber, setPdpNumber] = useState('')
  const [pdpExpiry, setPdpExpiry] = useState('')
  const [employmentType, setEmploymentType] = useState('')
  const [notes, setNotes] = useState('')

  function reseed() {
    setPhone(app.primary_phone ?? '')
    setEmail(app.primary_email ?? '')
    setWhatsapp(app.whatsapp_phone ?? '')
    setLicenseNumber('')
    setLicenseExpiry('')
    setPdpNumber('')
    setPdpExpiry('')
    setEmploymentType('')
    setNotes('')
  }

  async function handleSave() {
    const person_patch = {
      primary_phone: nullIfEmpty(phone),
      primary_email: nullIfEmpty(email),
      whatsapp_phone: nullIfEmpty(whatsapp),
    }
    const driver_patch: Record<string, unknown> = {}
    if (hasDriverRow) {
      addIfSet(driver_patch, 'license_number', licenseNumber)
      addIfSet(driver_patch, 'license_expiry', licenseExpiry)
      addIfSet(driver_patch, 'pdp_number', pdpNumber)
      addIfSet(driver_patch, 'pdp_expiry', pdpExpiry)
      addIfSet(driver_patch, 'employment_type', employmentType)
      addIfSet(driver_patch, 'notes', notes)
    }
    try {
      await update.trigger({
        application_id: app.application_id,
        person_patch,
        driver_patch,
      }).result
      toast.success('Driver details updated')
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not update driver details', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reseed()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <Pencil className="h-3.5 w-3.5" />
          Edit driver details
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit driver details</DialogTitle>
          <DialogDescription>{app.full_name || 'Applicant'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Contact
            </h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="drv-phone">Primary phone</Label>
                <Input id="drv-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="drv-email">Primary email</Label>
                <Input id="drv-email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="drv-whatsapp">WhatsApp phone</Label>
                <Input
                  id="drv-whatsapp"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                />
              </div>
            </div>
          </div>

          {hasDriverRow && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Driver record
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="drv-license">License number</Label>
                  <Input
                    id="drv-license"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="drv-license-exp">License expiry</Label>
                  <Input
                    id="drv-license-exp"
                    type="date"
                    value={licenseExpiry}
                    onChange={(e) => setLicenseExpiry(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="drv-pdp">PDP number</Label>
                  <Input
                    id="drv-pdp"
                    value={pdpNumber}
                    onChange={(e) => setPdpNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="drv-pdp-exp">PDP expiry</Label>
                  <Input
                    id="drv-pdp-exp"
                    type="date"
                    value={pdpExpiry}
                    onChange={(e) => setPdpExpiry(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="drv-employment">Employment type</Label>
                  <NativeSelect
                    id="drv-employment"
                    value={employmentType}
                    onChange={(e) => setEmploymentType(e.target.value)}
                  >
                    <option value="">Leave unchanged</option>
                    {EMPLOYMENT_TYPE_OPTIONS.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="drv-notes">Notes</Label>
                  <Textarea
                    id="drv-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Driver-record fields are write-only here; blank fields are left unchanged.
              </p>
              {/* Status is not edited here (audit B10). A free status dropdown let any
                  status be set with no transition rule, no offboarding case and no
                  history, while five triggers on drivers fired behind it. Status now
                  changes only through its named paths, and the database refuses a
                  status key from this dialog with the same sentence. */}
              <p className="mt-1 text-[11px] text-muted-foreground">
                Status is not changed here. Use the State panel on this driver&apos;s page (State, Reinstate to
                Active, Dismiss driver or Driver resigned), or open a case on the Offboarding page.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={update.loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={update.loading}>
            {update.loading && <Spinner className="h-4 w-4" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
